import type { ToolSpec, ToolCluster } from "./types";
import { FITNESS_TOOLS } from "./tools.fitness";
import { FINANCE_TOOLS } from "./tools.finance";
import { DATETIME_TOOLS } from "./tools.datetime";
import { DIGITAL_TOOLS } from "./tools.digital";
import { LIFESTYLE_TOOLS } from "./tools.lifestyle";
import { CREATOR_TOOLS } from "./tools.creator";
import { MAKER_TOOLS } from "./tools.maker";
import { HOMEYARD_TOOLS } from "./tools.homeyard";
import { HEALTH_TOOLS } from "./tools.health";
import { MONEY_TOOLS } from "./tools.money";
import { BUILD_TOOLS } from "./tools.build";
import { AUTO_TOOLS } from "./tools.auto";
import { POWER_TOOLS } from "./tools.power";
import { CRAFT_TOOLS } from "./tools.craft";
import { HOMESTEAD_TOOLS } from "./tools.homestead";
import { TECH_TOOLS } from "./tools.tech";
import { BUSINESS_TOOLS } from "./tools.business";

/** Batch 2 (50 new calculators) — surfaced as a "New Tools" block on hubs. */
export const NEW_TOOL_CLUSTERS: ToolCluster[] = [
  "build",
  "auto",
  "power",
  "craft",
  "homestead",
  "tech",
  "business",
];

export const ALL_TOOLS: ToolSpec[] = [
  ...FITNESS_TOOLS,
  ...FINANCE_TOOLS,
  ...DATETIME_TOOLS,
  ...DIGITAL_TOOLS,
  ...LIFESTYLE_TOOLS,
  ...CREATOR_TOOLS,
  ...MAKER_TOOLS,
  ...HOMEYARD_TOOLS,
  ...HEALTH_TOOLS,
  ...MONEY_TOOLS,
  ...BUILD_TOOLS,
  ...AUTO_TOOLS,
  ...POWER_TOOLS,
  ...CRAFT_TOOLS,
  ...HOMESTEAD_TOOLS,
  ...TECH_TOOLS,
  ...BUSINESS_TOOLS,
];

/** The 50 batch-2 calculators, in spec order. */
export const NEW_TOOLS: ToolSpec[] = [
  ...BUILD_TOOLS,
  ...AUTO_TOOLS,
  ...POWER_TOOLS,
  ...CRAFT_TOOLS,
  ...HOMESTEAD_TOOLS,
  ...TECH_TOOLS,
  ...BUSINESS_TOOLS,
];

export const TOOL_MAP: Record<string, ToolSpec> = Object.fromEntries(
  ALL_TOOLS.map((t) => [t.slug, t]),
);

export const ALL_TOOL_SLUGS: string[] = ALL_TOOLS.map((t) => t.slug);

/**
 * Topical focus guard (Sept 2026).
 *
 * DrHint's subject-matter authority is money: live gold/silver rates,
 * currency, bills, prize bonds and date/age maths. Clusters outside that
 * remit (tile grout, e-scooters, cheese making, gaming sensitivity…) are
 * genuinely useful to visitors who land on them, but they are off-topic
 * for this domain and read as scaled off-subject publishing to search
 * engines. They stay fully reachable and linked, but they are marked
 * `noindex, follow` and kept out of the sitemap so the indexed footprint
 * matches what this site is actually an authority on.
 */
export const INDEXABLE_CLUSTERS: ReadonlySet<ToolCluster> = new Set<ToolCluster>([
  "fitness",
  "finance",
  "datetime",
  "digital",
  "lifestyle",
  "build",
  "auto",
  "power",
  "craft",
  "homestead",
  "tech",
  "business",
]);

export function isToolIndexable(spec: ToolSpec | undefined): boolean {
  return !!spec && INDEXABLE_CLUSTERS.has(spec.cluster);
}

/** Tool slugs advertised in the sitemap. */
export const INDEXABLE_TOOL_SLUGS: string[] = ALL_TOOLS.filter((t) =>
  INDEXABLE_CLUSTERS.has(t.cluster),
).map((t) => t.slug);


export const CLUSTER_LABEL: Record<ToolCluster, string> = {
  fitness: "Health & Fitness",
  finance: "Money & Finance",
  datetime: "Date & Time",
  digital: "Digital & Creator",
  lifestyle: "Home & Lifestyle",
  build: "Home Renovation & Trade Math",
  auto: "Automotive, Towing & Powersports",
  power: "Off-Grid Power, Water & Marine",
  craft: "Culinary, Fermentation & Craft",
  homestead: "Horticulture & Homestead",
  tech: "Tech, Audio & Electronics",
  business: "Niche Business & Money Ops",
};

export const CLUSTER_EMOJI: Record<ToolCluster, string> = {
  fitness: "💪",
  finance: "💰",
  datetime: "📅",
  digital: "🎬",
  lifestyle: "🏡",
  build: "🧱",
  auto: "🚙",
  power: "🔋",
  craft: "🧀",
  homestead: "🌱",
  tech: "🖥️",
  business: "🏪",
};

export const CLUSTERS: ToolCluster[] = [
  "fitness",
  "finance",
  "datetime",
  "digital",
  "lifestyle",
  "build",
  "auto",
  "power",
  "craft",
  "homestead",
  "tech",
  "business",
];

export function toolsByCluster(c: ToolCluster): ToolSpec[] {
  return ALL_TOOLS.filter((t) => t.cluster === c);
}

export function getTool(slug: string): ToolSpec | undefined {
  return TOOL_MAP[slug];
}

/** Deterministic related-tool picks so SSR and client agree. */
export function relatedTools(spec: ToolSpec, count = 6): ToolSpec[] {
  const explicit = (spec.related ?? []).map((s) => TOOL_MAP[s]).filter(Boolean) as ToolSpec[];
  const sameCluster = toolsByCluster(spec.cluster).filter(
    (t) => t.slug !== spec.slug && !explicit.some((e) => e.slug === t.slug),
  );
  const rest = ALL_TOOLS.filter(
    (t) => t.slug !== spec.slug && t.cluster !== spec.cluster,
  );
  return [...explicit, ...sameCluster, ...rest].slice(0, count);
}
